import { useEffect } from 'react';
import { reportClientError } from '../services/appwrite/report-client-error';
import { recoverFromStaleDynamicImport } from './stale-dynamic-import';

/**
 * Ningún error del cliente debería quedar silencioso — ver
 * `report-client-error.ts`. Esto cubre lo que nadie más atrapó: un error de
 * JavaScript sin manejar, o una promesa rechazada sin `.catch`. No repara
 * nada; solo asegura que quede visible en "Estado del sistema" en vez de
 * perderse en la consola de quien lo sufrió.
 */
export function GlobalErrorReporter() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      if (recoverFromStaleDynamicImport(event.error ?? event.message)) {
        event.preventDefault();
        return;
      }
      reportClientError({
        source: 'web-uncaught',
        operation: 'window.onerror',
        message: event.message || 'Unknown error',
        details: { filename: event.filename, lineno: event.lineno, colno: event.colno, stack: event.error?.stack },
      });
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      if (recoverFromStaleDynamicImport(reason)) {
        event.preventDefault();
        return;
      }
      reportClientError({
        source: 'web-uncaught',
        operation: 'unhandledrejection',
        message: reason instanceof Error ? reason.message : String(reason),
        details: { stack: reason instanceof Error ? reason.stack : undefined },
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
